const DIAL_CX = 280;
const DIAL_CY = 244;
const SLOT_COUNT = 12;
const LABEL_GAP = 16;
const LEFT_LABEL_GAP = 8;
const LOWER_LABEL_GAP = 12;
const LABEL_BOX_WIDTH = 120;
const LABEL_BOX_HEIGHT = 64;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const WAIT = 0.4;
const DRAW = 1.1;
const MARKER_FADE = 0.2;
const HOLD = 1.3;
const EXIT = 0.7;
const PHASE = WAIT + DRAW + HOLD + EXIT;
const OPEN_EASE_X2 = 0.199124;
const CLOSE_EASE_X2 = 0.202344;

// Curve of the established Circadian easing: cubic-bezier(0, 0, x2, 1).
const bezierX = (s, x2) => (3 * (1 - s) * x2 + s) * s * s;
const bezierY = (s) => (3 - 2 * s) * s * s;

function solve(fn, target) {
  let low = 0;
  let high = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (low + high) / 2;
    if (fn(mid) < target) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

const circadianEase = (x2) => (progress) => {
  if (progress <= 0) return 0;
  if (progress >= 1) return 1;
  return bezierY(solve((s) => bezierX(s, x2), progress));
};

// Time fraction of the draw at which the eased stroke reaches `progress`.
const drawTimeAt = (progress) => bezierX(solve(bezierY, progress), OPEN_EASE_X2);

const slotOf = (marker) => Math.min(SLOT_COUNT, Math.max(1, Math.round(Number(marker.dataset.clockPosition)) || 1));

class FeatureContentClock extends HTMLElement {
  static observedAttributes = ['playing'];

  #context = null;
  #timeline = null;
  #onLoad = null;
  #reducedMotionQuery = null;
  #onReducedMotionChange = null;

  connectedCallback() {
    this.#placeMarkers();
    this.dataset.ready = '';

    this.#reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    this.#onReducedMotionChange = () => this.#update();
    this.#reducedMotionQuery.addEventListener('change', this.#onReducedMotionChange);

    if (window.gsap || document.readyState === 'complete') {
      this.#update();
      return;
    }
    this.#onLoad = () => this.#update();
    window.addEventListener('load', this.#onLoad, { once: true });
  }

  disconnectedCallback() {
    if (this.#onLoad) window.removeEventListener('load', this.#onLoad);
    this.#onLoad = null;
    this.#reducedMotionQuery?.removeEventListener('change', this.#onReducedMotionChange);
    this.#reducedMotionQuery = null;
    this.#onReducedMotionChange = null;
    this.#stop();
  }

  attributeChangedCallback() {
    this.#update();
  }

  #update() {
    if (!this.#reducedMotionQuery || !window.gsap) return;

    if (this.#reducedMotionQuery.matches) {
      this.#stop();
      return;
    }

    this.#timeline ??= this.#build();
    if (this.hasAttribute('playing')) this.#timeline.play();
    else this.#timeline.pause();
  }

  #stop() {
    // Reverting restores the static complete clock (all quadrants and callouts visible).
    this.#context?.revert();
    this.#context = null;
    this.#timeline = null;
  }

  #build() {
    const { gsap } = window;
    const openEase = circadianEase(OPEN_EASE_X2);
    const closeEase = circadianEase(CLOSE_EASE_X2);
    let timeline;

    this.#context = gsap.context(() => {
      timeline = gsap.timeline({ repeat: -1, paused: true });

      this.querySelectorAll('.feature-content-clock__quadrant').forEach((quadrant, index) => {
        const arc = quadrant.querySelector('.feature-content-clock__arc');
        const marker = quadrant.querySelector('.feature-content-clock__marker');
        const start = index * PHASE;
        const exitAt = start + WAIT + DRAW + HOLD;
        if (!arc) return;

        // Arcs run counterclockwise: dashes grow from the path end so the line draws clockwise.
        gsap.set(arc, { opacity: 0, strokeDasharray: '0 1', strokeDashoffset: -1 });
        timeline
          .set(arc, { opacity: 1 }, start + WAIT)
          // autoRound would snap the fractional (pathLength="1") dashoffset to whole pixels.
          .to(
            arc,
            { strokeDasharray: '1 1', strokeDashoffset: 0, autoRound: false, duration: DRAW, ease: openEase },
            start + WAIT
          )
          .to(arc, { strokeDasharray: '0 1', duration: EXIT, ease: closeEase }, exitAt)
          .set(arc, { opacity: 0 }, start + PHASE);

        if (!marker) return;
        const dot = marker.querySelector('.feature-content-clock__dot');
        const revealAt = start + WAIT + drawTimeAt((slotOf(marker) - 1) / (SLOT_COUNT - 1)) * DRAW;

        gsap.set(marker, { opacity: 0 });
        timeline
          .to(marker, { opacity: 1, duration: MARKER_FADE, ease: 'power1.out' }, revealAt)
          .to(marker, { opacity: 0, duration: EXIT, ease: 'power1.in' }, exitAt);
        if (dot) timeline.to(dot, { attr: { r: 2 }, duration: EXIT, ease: 'power1.in' }, exitAt);
      });
    }, this);

    return timeline;
  }

  #placeMarkers() {
    this.querySelectorAll('.feature-content-clock__quadrant').forEach((quadrant) => {
      const arc = quadrant.querySelector('.feature-content-clock__arc');
      const marker = quadrant.querySelector('.feature-content-clock__marker');
      if (!arc || !marker) return;

      const pathLength = arc.getTotalLength();
      if (!pathLength) return;

      const slot = slotOf(marker);
      // Arcs are drawn counterclockwise, so slot 1 (first clockwise) sits at the path end.
      const point = arc.getPointAtLength(((SLOT_COUNT - slot) / (SLOT_COUNT - 1)) * pathLength);
      const labelGap = quadrant.dataset.quadrant === '24_6' ? LEFT_LABEL_GAP : LABEL_GAP;

      const dx = point.x - DIAL_CX;
      const dy = point.y - DIAL_CY;
      const distance = Math.hypot(dx, dy) || 1;
      const onRight = dx >= 0;
      const belowMarker = dy > 0;
      const labelY = point.y + (dy / distance) * labelGap;

      const dot = marker.querySelector('.feature-content-clock__dot');
      dot?.setAttribute('cx', String(point.x));
      dot?.setAttribute('cy', String(point.y));

      const labelBox = marker.querySelector('.feature-content-clock__label-box');
      labelBox?.setAttribute('x', String(onRight ? point.x + labelGap : point.x - labelGap - LABEL_BOX_WIDTH));
      labelBox?.setAttribute('y', String(belowMarker ? point.y + LOWER_LABEL_GAP : labelY - LABEL_BOX_HEIGHT / 2));

      const label = marker.querySelector('.feature-content-clock__label');
      if (label) {
        label.style.textAlign = onRight ? 'left' : 'right';
        label.style.justifyContent = onRight ? 'flex-start' : 'flex-end';
        label.style.alignItems = belowMarker ? 'flex-start' : 'center';
      }
    });
  }
}

if (!customElements.get('feature-content-clock')) {
  customElements.define('feature-content-clock', FeatureContentClock);
}
